import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { loginStart, loginSuccess, loginFailure } from '../../store/slices/auth.slice';
import authApi from '../../services/api/auth.api';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import {
  ChartBarIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { config: whiteLabelConfig, shouldShowWhiteLabel } = useWhiteLabel();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    dispatch(loginStart());

    try {
      const response = await authApi.login(formData);

      if (response.success) {
        dispatch(
          loginSuccess({
            user: response.data.user,
            token: response.data.token,
            refreshToken: response.data.refreshToken,
          })
        );
        toast.success(t('login.success') || 'Login realizado com sucesso!', {
          icon: '✅',
          duration: 2000,
        });

        // Limpar qualquer estado residual de sessões anteriores
        // Limpar query cache e outros caches do navegador
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach((name) => {
              caches.delete(name);
            });
          });
        }

        // Limpar sessionStorage também para evitar resquícios
        sessionStorage.clear();

        // Usar window.location ao invés de navigate() para forçar reload completo
        // Isso garante que toda a aplicação será reinicializada com a nova sessão
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 800);
      } else {
        const errorMsg = t('login.error');
        toast.error(errorMsg, {
          duration: 5000,
          icon: '❌',
        });
        dispatch(loginFailure(errorMsg));
      }
    } catch (err: any) {
      // Check for 401 Unauthorized - wrong credentials
      const status = err.response?.status;
      let errorMsg: string;

      if (status === 401) {
        errorMsg = t('login.invalidCredentials');
      } else {
        errorMsg = err.response?.data?.error || err.message || t('login.error');
      }

      // Show toast notification
      toast.error(errorMsg, {
        duration: 5000,
        icon: '❌',
      });

      dispatch(loginFailure(errorMsg));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-12 flex-col justify-between">
        <div>
          {shouldShowWhiteLabel && whiteLabelConfig.custom_name ? (
            <div className="flex items-center gap-3 text-white">
              <ChartBarIcon className="h-10 w-10" />
              <span className="text-2xl font-bold">{whiteLabelConfig.custom_name}</span>
            </div>
          ) : (
            <Link to="/" className="flex items-center gap-3 text-white">
              <ChartBarIcon className="h-10 w-10" />
              <span className="text-2xl font-bold">Kubmix Flux</span>
            </Link>
          )}
        </div>

        <div className="text-white">
          <h1 className="text-5xl font-bold mb-6">
            {t('login.hero.title')}
            <br />
            {t('login.hero.titleLine2')}
          </h1>
          <p className="text-xl text-indigo-100 mb-8">
            {t('login.hero.subtitle')}
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                ✓
              </div>
              <span className="text-lg">{t('login.hero.feature1')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                ✓
              </div>
              <span className="text-lg">{t('login.hero.feature2')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                ✓
              </div>
              <span className="text-lg">{t('login.hero.feature3')}</span>
            </div>
          </div>
        </div>

        <div className="text-indigo-100 text-sm">
          {t('login.hero.copyright')}
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        {/* Language Switcher */}
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            {shouldShowWhiteLabel && whiteLabelConfig.custom_name ? (
              <div className="inline-flex items-center gap-2">
                <ChartBarIcon className="h-8 w-8 text-indigo-600" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">{whiteLabelConfig.custom_name}</span>
              </div>
            ) : (
              <Link to="/" className="inline-flex items-center gap-2">
                <ChartBarIcon className="h-8 w-8 text-indigo-600" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">Kubmix Flux</span>
              </Link>
            )}
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('login.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('login.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('login.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="block w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                  placeholder={t('login.emailPlaceholder')}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('login.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="block w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                  placeholder={t('login.passwordPlaceholder')}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  {t('login.rememberMe')}
                </label>
              </div>

              <Link to="/forgot-password" className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
                {t('login.forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t('login.signingIn')}
                </>
              ) : (
                <>
                  {t('login.signIn')}
                  <ArrowRightIcon className="h-5 w-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                  {t('login.noAccount')}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/register"
                className="w-full flex items-center justify-center px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-base font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('login.startTrial')}
              </Link>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
            <Link to="https://flux.devskin.com" className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
              {t('login.backToHome')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
